/** @jsxImportSource @emotion/react */
import { Button, InputGroup, Intent, Spinner } from "@blueprintjs/core"
import { IconNames } from "@blueprintjs/icons"
import { capitalize } from "lodash"
import { useEffect, useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { createToaster } from "../../../utils/createToaster"
import { DollarAmountInput } from "./components/DollarAmountInput"

import { DecimalInput, InputWrapper, IntegerInput, TimeInput } from "../../../components/rhf"
import { WaveSelect } from "./components/WaveSelect"
import useOrderQuery from "./hooks/useOrderQuery"
import useReplaceOrderMutation from "./hooks/useReplaceOrderMutation"
import {
  AmountInputWrapper,
  CurrencySwitchButton,
  ErrorContainer,
  FooterActions,
  FormContainer,
  FormGroupStyled,
  FormSectionDivider,
  IconWrapper,
  InputContainer,
  OrderDetailItem,
  OrderDetailLabel,
  OrderDetailsContainer,
  OrderDetailValue,
  ReplaceButton,
  SelectWrapper,
  SpinnerContainer,
  StyledDialog,
} from "./ReplaceOrderModal.styled"
import { Broker } from "./types/brokers"
import { OrderStatus, OrderType } from "./types/common"
import { getExecutionTypesForBrokerAndOrderType } from "./types/execution"
import { OrderInputs } from "./types/form"

interface ReplaceOrderModalProps {
  isOpen: boolean
  onClose: () => void
  orderId: string
}

const ReplaceOrderModal = ({ isOpen, onClose, orderId }: ReplaceOrderModalProps) => {
  const { order, isLoading, error } = useOrderQuery({ orderId })

  // State to track whether quantity or USD is being used
  const [usingQuantity, setUsingQuantity] = useState(true)

  // Set up the form with default values from the order
  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<Partial<OrderInputs>>({
    defaultValues: {
      quantity: undefined,
      amount_usd: undefined,
      order_type: undefined,
      limit_price: undefined,
      broker: undefined,
      execution_type: undefined,
      target_end_datetime: undefined,
      execution_details: undefined,
    },
    mode: "onChange",
  })

  // Reset form with values when order data is loaded
  useEffect(() => {
    if (order) {
      reset({
        quantity: order.quantity,
        amount_usd: order.amount_usd,
        order_type: order.order_type,
        limit_price: order.limit_price,
        broker: order.broker,
        execution_type: order.execution_type,
        target_end_datetime: order.target_end_datetime,
        execution_details: order.execution_details,
      })
    }
  }, [order, reset])

  // Watch values for validation
  const watchOrderType = watch("order_type")
  const watchBroker = watch("broker")

  /**
   * Effect to fill limit from latest price if order type is limit
   */
  useEffect(() => {
    if (watchOrderType === OrderType.LIMIT) {
      setValue("limit_price", order?.instrument?.latest_price)
    }
  }, [watchOrderType, order, setValue])

  // Set up the mutation for patching the order
  const replaceOrderMutation = useReplaceOrderMutation({
    onSuccess: updatedOrder => {
      if (updatedOrder.status === OrderStatus.REPLACE_FAILED) {
        createToaster({
          message: `Order failed to replace, ${updatedOrder.execution_summary}`,
          intent: Intent.DANGER,
        })
      } else {
        createToaster({
          message: "Order replaced successfully",
          intent: Intent.SUCCESS,
        })
      }
      onClose()
    },
    onError: error => {
      createToaster({
        message: `Error replacing order, please contact support`,
        intent: Intent.DANGER,
        timeout: 5000,
      })
    },
  })

  // Handle form submission
  const onSubmit = (data: OrderInputs) => {
    replaceOrderMutation.mutate({
      orderId,
      values: {
        quantity: usingQuantity ? data.quantity : undefined,
        amount_usd: !usingQuantity ? data.amount_usd : undefined,
        order_type: data.order_type,
        limit_price: data.limit_price,
        execution_type: data.execution_type,
      },
    })
  }

  // Toggle between quantity and USD
  const toggleInputType = () => {
    setUsingQuantity(!usingQuantity)
  }

  /**
   * Memoized options for execution type
   */
  const executionTypeOptions = useMemo<Record<string, string>>(
    () => getExecutionTypesForBrokerAndOrderType(watchBroker, watchOrderType),
    [watchBroker, watchOrderType],
  )

  if (isLoading) {
    return (
      <StyledDialog isOpen={isOpen} onClose={onClose} title="Replace Order" className="bp5-dark">
        <SpinnerContainer justifyContent="center" className="bp3-dialog-body">
          <Spinner />
        </SpinnerContainer>
      </StyledDialog>
    )
  }

  if (error || !order) {
    return (
      <StyledDialog isOpen={isOpen} onClose={onClose} title="Replace Order" className="bp5-dark">
        <ErrorContainer className="bp3-dialog-body">
          <p>
            {error?.response?.status === 404
              ? "Unable to identify order, if this order was created through Scient please contact the support"
              : "Error loading order details. Please try again."}
          </p>
          <Button onClick={onClose} text="Close" />
        </ErrorContainer>
      </StyledDialog>
    )
  }

  if (order && order.status === OrderStatus.REPLACE_FAILED) {
    return (
      <StyledDialog isOpen={isOpen} onClose={onClose} title="Replace Order" className="bp5-dark">
        <ErrorContainer className="bp3-dialog-body">
          <p>Order already failed to replace, cancel and create a new order or contact support</p>
        </ErrorContainer>
      </StyledDialog>
    )
  }

  return (
    <StyledDialog isOpen={isOpen} onClose={onClose} title="Replace Order" className="bp5-dark">
      <div className="bp3-dialog-body">
        <FormContainer onSubmit={handleSubmit(onSubmit)}>
          {/* Order Info Display */}
          <OrderDetailsContainer
            label={
              <span>
                <IconWrapper icon={IconNames.INFO_SIGN} />
                Order Details
              </span>
            }
          >
            <OrderDetailItem>
              <OrderDetailLabel>ID:</OrderDetailLabel>
              <OrderDetailValue>{orderId}</OrderDetailValue>
            </OrderDetailItem>
            <OrderDetailItem>
              <OrderDetailLabel>Symbol:</OrderDetailLabel>
              <OrderDetailValue>{order?.instrument?.bloomberg_code}</OrderDetailValue>
            </OrderDetailItem>
            <OrderDetailItem>
              <OrderDetailLabel>Side:</OrderDetailLabel>
              <OrderDetailValue>{capitalize(order?.side)}</OrderDetailValue>
            </OrderDetailItem>
            <OrderDetailItem>
              <OrderDetailLabel>Type:</OrderDetailLabel>
              <OrderDetailValue>{capitalize(order?.order_type)}</OrderDetailValue>
            </OrderDetailItem>
            {order?.order_type === OrderType.LIMIT && (
              <OrderDetailItem>
                <OrderDetailLabel>Limit Price:</OrderDetailLabel>
                <OrderDetailValue>{order?.limit_price}</OrderDetailValue>
              </OrderDetailItem>
            )}
          </OrderDetailsContainer>

          {/* Order Type */}
          <FormGroupStyled
            label={
              <span>
                <IconWrapper icon={IconNames.EXCHANGE} />
                Order Type
              </span>
            }
          >
            <SelectWrapper>
              <WaveSelect
                name="order_type"
                optionsEnum={OrderType}
                control={control as any}
                rules={{
                  required: "Order type is required",
                  deps: ["limit_price"],
                }}
              />
            </SelectWrapper>
          </FormGroupStyled>

          {/* Limit Price */}
          {watchOrderType === OrderType.LIMIT && (
            <FormGroupStyled
              label={
                <span>
                  <IconWrapper icon={IconNames.DOLLAR} />
                  Limit Price
                </span>
              }
            >
              <AmountInputWrapper>
                <InputWrapper error={errors?.limit_price}>
                  <DecimalInput
                    name="limit_price"
                    control={control}
                    rules={{
                      required: "Limit price is required",
                    }}
                  />
                </InputWrapper>
              </AmountInputWrapper>
            </FormGroupStyled>
          )}

          {/* Quantity or USD */}
          <FormGroupStyled
            label={
              <span>
                <IconWrapper icon={usingQuantity ? IconNames.NUMERICAL : IconNames.DOLLAR} />
                {usingQuantity ? "Quantity" : "USD Amount"}
              </span>
            }
          >
            <InputContainer alignItems="center" gap="10px">
              <AmountInputWrapper>
                {usingQuantity ? (
                  <InputWrapper error={errors?.quantity}>
                    <IntegerInput
                      registerReturn={register("quantity", {
                        required: "Quantity is required",
                        min: {
                          value: 1,
                          message: "Minimum quantity is 1",
                        },
                      })}
                    />
                  </InputWrapper>
                ) : (
                  <DollarAmountInput
                    name="amount_usd"
                    index={0}
                    control={control as any}
                    rules={{
                      max: {
                        value: 5000000,
                        message: "USD is limited to 5M",
                      },
                    }}
                  />
                )}
              </AmountInputWrapper>
              <CurrencySwitchButton
                small
                icon={usingQuantity ? IconNames.DOLLAR : IconNames.NUMERICAL}
                onClick={toggleInputType}
                text={usingQuantity ? "Switch to USD" : "Switch to Quantity"}
              />
            </InputContainer>
          </FormGroupStyled>
          <FormSectionDivider />

          {/* Broker */}
          <FormGroupStyled
            label={
              <span>
                <IconWrapper icon={IconNames.OFFICE} />
                Broker
              </span>
            }
          >
            <SelectWrapper>
              <WaveSelect
                name="broker"
                optionsEnum={Broker}
                control={control as any}
                disabled={true}
              />
            </SelectWrapper>
          </FormGroupStyled>

          {/* Execution Type */}
          <FormGroupStyled
            label={
              <span>
                <IconWrapper icon={IconNames.DASHBOARD} />
                Execution Type
              </span>
            }
          >
            <SelectWrapper>
              <WaveSelect
                name="execution_type"
                optionsEnum={executionTypeOptions}
                control={control as any}
                rules={{
                  validate: value => {
                    if (!value && watchBroker !== Broker.TOUR) {
                      return "Execution type is required"
                    }
                    return true
                  },
                }}
              />
            </SelectWrapper>
          </FormGroupStyled>
          <FormGroupStyled
            label={
              <span>
                <IconWrapper icon={IconNames.TIME} />
                Time in Market
              </span>
            }
          >
            <TimeInput registerReturn={register(`target_end_datetime`)} />
          </FormGroupStyled>

          {/* Execution Details */}
          <FormGroupStyled
            label={
              <span>
                <IconWrapper icon={IconNames.INFO_SIGN} />
                Execution Details
              </span>
            }
          >
            <Controller
              name="execution_details"
              control={control}
              render={({ field }) => (
                <InputGroup
                  {...field}
                  large={true}
                  intent={errors.execution_details ? "danger" : "none"}
                />
              )}
            />
          </FormGroupStyled>

          <div className="bp3-dialog-footer">
            <FooterActions>
              <Button onClick={onClose} icon={IconNames.CROSS}>
                Cancel
              </Button>
              <ReplaceButton
                type="submit"
                intent="primary"
                loading={replaceOrderMutation.isLoading}
                disabled={Object.keys(errors).length > 0}
                icon={IconNames.REFRESH}
              >
                Replace Order
              </ReplaceButton>
            </FooterActions>
          </div>
        </FormContainer>
      </div>
    </StyledDialog>
  )
}

export default ReplaceOrderModal
